import React from 'react';
import cx from 'classnames';

import { DataCardRow } from '../components';
import { PerformancesTableButton } from '../../../../../nav';

interface TopFiveProgramsRowProps {
    /**
     * Color to use for earnings amount
     */
    earningsColor: 'secondary' | 'quarternary';
    /**
     * Earnings formatted as a dollar amount
     */
    earningsFormatted: string;
    /**
     * Index of this item within its data array
     */
    index: number;
    /**
     * URL search parameters for updating the show and source (if source exists)
     */
    searchParams: URLSearchParam[] | null;
    /**
     * Show name
     */
    show: string;
    /**
     * Source name, if any
     */
    source: Maybe<string>;
}

/**
 * Renders a table row as a button. Intended for use within TopFiveProgramsCard.
 * @param props - see {@link TopFiveProgramsRowProps}
 */
export const TopFiveProgramsRow = (
    props: TopFiveProgramsRowProps,
): JSX.Element => {
    const { earningsColor, earningsFormatted, index, searchParams, source } =
        props;

    const hasSource =
        (source?.length ?? 0) > 0 && source?.toLowerCase() !== 'none';
    let { show } = props;

    if (show === 'not_reported' || show === 'not reported') {
        if (hasSource) {
            show = 'Unknown show';
        } else {
            show = '';
        }
    }

    const hasShow = show.length > 0;

    const gridTemplateColumns = hasSource && hasShow ? '55px auto' : 'auto';

    const showDetails = (
        <div
            className="grid"
            style={{
                gridTemplateColumns,
            }}
        >
            {hasSource && (
                <div
                    className={cx('text-left mr-1 truncate', {
                        'font-medium': !hasShow,
                        'font-semibold': hasShow,
                    })}
                >
                    {source}
                </div>
            )}
            {hasShow && (
                <div
                    className={cx('text-left truncate', {
                        'font-medium': hasShow,
                        'font-semibold': !hasShow,
                    })}
                >
                    {show}
                </div>
            )}
        </div>
    );

    return (
        <DataCardRow index={index}>
            {searchParams && (
                <PerformancesTableButton
                    urlSearchParams={searchParams}
                    statementSelectionLogic={{
                        type: 'presetsWithRegion',
                        presetsWithRegion: {
                            primary: 'all_domestic',
                            fallback: 'all_international',
                        },
                    }}
                >
                    <div className="underline">{showDetails}</div>
                </PerformancesTableButton>
            )}
            {!searchParams && <>{showDetails}</>}

            <span className={`ml-2 font-semibold text-${earningsColor}-500`}>
                {earningsFormatted}
            </span>
        </DataCardRow>
    );
};
